import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { EPOCH_VERSION } from "../../../lib/epoch-version";
import type { FooterPanelTab } from "./footer-toolbar-model";
import {
  compactFooterPrompt,
  FOOTER_SLASH_COMMANDS,
  resolveFooterSlashCommand,
  type FooterSlashCommand,
} from "./footer-terminal-commands";
import { useFooterTerminalProcess } from "./use-footer-terminal-process";
import { runFooterDesktopUpdate } from "./footer-terminal-update";

export interface FooterTerminalLine {
  type: "input" | "output";
  text: string;
}

export interface FooterTerminalController {
  handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  history: FooterTerminalLine[];
  input: string;
  inputRef: RefObject<HTMLInputElement | null>;
  onInputChange: (value: string) => void;
  prompt: string;
  setInput: (value: string) => void;
  suggestions: FooterSlashCommand[];
  suggestionsIndex: number;
  suggestionsOpen: boolean;
  terminalEndRef: RefObject<HTMLDivElement | null>;
  useSuggestion: (item: FooterSlashCommand) => void;
}

const WELCOME =
  'Fintheon CLI, type any shell command. "help" for built-ins, / for shortcuts.';

export function useFooterTerminal(
  panelOpen: boolean,
  activeTab: FooterPanelTab,
  setActiveTab: (tab: FooterPanelTab) => void,
): FooterTerminalController {
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<FooterTerminalLine[]>([
    { type: "output", text: WELCOME },
  ]);
  const [cwd, setCwd] = useState("fintheon");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestionsIndex, setSuggestionsIndex] = useState(0);
  const terminalEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const process = useFooterTerminalProcess(setHistory, setCwd);
  const filter = input.startsWith("/")
    ? input.slice(1).toLowerCase().trim()
    : "";
  const suggestions = useMemo(
    () =>
      filter
        ? FOOTER_SLASH_COMMANDS.filter(
            (item) =>
              item.slug.toLowerCase().includes(filter) ||
              item.label.toLowerCase().includes(filter),
          )
        : FOOTER_SLASH_COMMANDS,
    [filter],
  );

  useEffect(() => setSuggestionsIndex(0), [filter, suggestions.length]);
  useEffect(() => {
    terminalEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history]);
  useEffect(() => {
    if (panelOpen && activeTab === "terminal") inputRef.current?.focus();
  }, [activeTab, panelOpen]);
  useEffect(() => {
    if (!window.electron?.setCliOutputCallback) return;
    window.electron.setCliOutputCallback((event) => {
      if (event.type === "stdout" || event.type === "stderr") {
        const lines = String(event.data ?? "")
          .split("\n")
          .filter(Boolean);
        setHistory((current) => [
          ...current,
          ...lines.map((text) => ({ type: "output" as const, text })),
        ]);
      } else if (event.type === "exit") {
        setHistory((current) => [
          ...current,
          {
            type: "output",
            text: `[exit ${event.code ?? event.signal ?? "?"}]`,
          },
        ]);
      }
    });
    return () => window.electron?.setCliOutputCallback(null);
  }, []);

  const execute = useCallback(
    (command: string) => {
      const resolved = resolveFooterSlashCommand(command);
      const commandToRun = resolved ?? command;
      const next = [
        ...history,
        {
          type: "input" as const,
          text: `${compactFooterPrompt(cwd)} $ ${resolved ? command : commandToRun}`,
        },
      ];
      const lower = command.toLowerCase().trim();
      setInput("");
      setSuggestionsOpen(false);
      if (lower === "clear") {
        setHistory([{ type: "output", text: WELCOME }]);
      } else if (lower === "help") {
        setHistory([
          ...next,
          {
            type: "output",
            text: "Any line runs from the project root. Ctrl+C kills a process.",
          },
          {
            type: "output",
            text: "Built-ins: help, changelog, clear, status, version, update",
          },
        ]);
      } else if (lower === "changelog") {
        setHistory([
          ...next,
          { type: "output", text: "Switched to changelog tab." },
        ]);
        setActiveTab("changelog");
      } else if (lower === "status") {
        setHistory([
          ...next,
          {
            type: "output",
            text: "System: online | Backend: localhost:8080 | Agents: standby",
          },
        ]);
      } else if (lower === "version") {
        setHistory([
          ...next,
          { type: "output", text: `Fintheon Epoch ${EPOCH_VERSION}` },
        ]);
      } else if (lower === "update" || lower === "fintheon update") {
        setHistory([
          ...next,
          {
            type: "output",
            text: "Checking for updates via desktop updater...",
          },
        ]);
        runFooterDesktopUpdate(setHistory);
      } else {
        setHistory(next);
        process.run(commandToRun);
      }
    },
    [cwd, history, process, setActiveTab],
  );
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "c" && event.ctrlKey) {
        event.preventDefault();
        process.kill();
        return;
      }
      if (suggestionsOpen && suggestions.length) {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          const delta = event.key === "ArrowDown" ? 1 : -1;
          setSuggestionsIndex(
            (index) =>
              (index + delta + suggestions.length) % suggestions.length,
          );
          return;
        }
        if (event.key === "Enter") {
          event.preventDefault();
          useSuggestion(suggestions[suggestionsIndex]);
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          setSuggestionsOpen(false);
          return;
        }
      }
      if (event.key === "Enter" && input.trim()) execute(input.trim());
    },
    [execute, input, process, suggestions, suggestionsIndex, suggestionsOpen],
  );
  const onInputChange = (value: string) => {
    setInput(value);
    setSuggestionsOpen(value.startsWith("/"));
  };
  const useSuggestion = (item: FooterSlashCommand) => {
    setInput(item.command);
    setSuggestionsOpen(false);
    inputRef.current?.focus();
  };
  return {
    handleKeyDown,
    history,
    input,
    inputRef,
    onInputChange,
    prompt: compactFooterPrompt(cwd),
    setInput,
    suggestions,
    suggestionsIndex,
    suggestionsOpen:
      suggestionsOpen && (input === "/" || suggestions.length > 0),
    terminalEndRef,
    useSuggestion,
  };
}
